import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { MdEdit } from 'react-icons/md';
import { SearchableSelect } from './SearchableSelect';
import { CATEGORY_NAME_MAX_LEN, validateCategoryName } from '../../utils/validation';
import {
  useCreateCategoryMutation,
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
  useUpdateCategoryStatusMutation,
} from '../../redux/categoryApi';
import { useGetVendorsQuery } from '../../redux/vendorApi';
import { useGetActiveDepartmentsQuery } from '../../redux/employeeApi';
import { useGetAccountableDepartmentsQuery } from '../../redux/complaintApi';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';

const STATUS_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

function AdminCategories() {
  const { canAction } = useAuth();
  const canCreate = canAction('categories', 'create');
  const canEdit = canAction('categories', 'edit');
  const canDelete = canAction('categories', 'delete');
  const hasActions = canEdit || canDelete;
  const tableColSpan = hasActions ? 6 : 5;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryComplainType, setNewCategoryComplainType] = useState('');
  const [newVendorId, setNewVendorId] = useState('');
  const [newDepartmentId, setNewDepartmentId] = useState('');
  const [error, setError] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryComplainType, setEditCategoryComplainType] = useState('');
  const [editVendorId, setEditVendorId] = useState('');
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [editError, setEditError] = useState('');
  const pageSize = DEFAULT_PAGE_SIZE;
  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (filter === 'active') params.status = true;
    else if (filter === 'inactive') params.status = false;
    return params;
  }, [page, pageSize, search, filter]);
  const { data: categoriesRes, isFetching, isLoading, error: fetchError, refetch } =
    useGetCategoriesQuery(listQuery);
  const { data: vendorsRes } = useGetVendorsQuery();
  const { data: allDepartmentsRes } = useGetActiveDepartmentsQuery();
  const formComplaintType = (() => {
    const raw = isEditOpen
      ? editCategoryComplainType
      : isAddOpen
        ? newCategoryComplainType
        : '';
    const type = String(raw || '').toUpperCase();
    return type === 'CTM' || type === 'ITSM' ? type : '';
  })();
  const { data: filteredDepartmentsRes } = useGetAccountableDepartmentsQuery(
    { complaint_type: formComplaintType },
    { skip: !formComplaintType }
  );
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [updateCategoryStatus, { isLoading: isUpdatingStatus }] =
    useUpdateCategoryStatusMutation();

  const vendorNameById = useMemo(() => {
    const list = vendorsRes?.data || vendorsRes || [];
    const map = new Map();
    (Array.isArray(list) ? list : []).forEach((vendor) => {
      const id = vendor?.id;
      if (id == null) return;
      map.set(String(id), vendor?.name ?? '');
    });
    return map;
  }, [vendorsRes]);

  const departmentNameById = useMemo(() => {
    const list = allDepartmentsRes?.data || allDepartmentsRes || [];
    const map = new Map();
    (Array.isArray(list) ? list : []).forEach((dept) => {
      const id = dept?.id;
      if (id == null) return;
      map.set(
        String(id),
        dept?.name ??
          dept?.department_name ??
          dept?.title ??
          dept?.department?.name ??
          ''
      );
    });
    return map;
  }, [allDepartmentsRes]);

  const vendorOptions = useMemo(() => {
    const list = vendorsRes?.data || vendorsRes || [];
    return (Array.isArray(list) ? list : [])
      .slice()
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
      .map((vendor) => ({
        value: String(vendor.id),
        label: vendor.name ?? String(vendor.id),
      }));
  }, [vendorsRes]);

  const departmentOptions = useMemo(() => {
    if (!formComplaintType) return [];
    const list = filteredDepartmentsRes?.data || filteredDepartmentsRes || [];
    return (Array.isArray(list) ? list : [])
      .slice()
      .sort((a, b) =>
        String(a?.name || a?.department_name || '').localeCompare(
          String(b?.name || b?.department_name || '')
        )
      )
      .map((dept) => ({
        value: String(dept.id),
        label:
          dept.name ??
          dept.department_name ??
          dept.title ??
          dept.department?.name ??
          String(dept.id),
      }));
  }, [filteredDepartmentsRes, formComplaintType]);

  const categories = useMemo(() => {
    const rows = [...(categoriesRes?.data || [])].sort(
      (a, b) => Number(b.id) - Number(a.id)
    );
    return rows.map((cat) => {
      const vendorId =
        cat.vendor_id ?? cat.vendorId ?? cat.vendor?.id ?? '';
      const departmentId =
        cat.accountable_department_id ??
        cat.accountableDepartmentId ??
        cat.department_id ??
        cat.departmentId ??
        cat.accountable_department?.id ??
        cat.department?.id ??
        '';
      return {
        id: cat.id,
        name: cat.name,
        category_complain_type: cat.category_complain_type || '',
        vendorId: vendorId != null && vendorId !== '' ? String(vendorId) : '',
        vendorName:
          cat.vendor_name ||
          cat.vendorName ||
          cat.vendor?.name ||
          vendorNameById.get(String(vendorId)) ||
          '',
        departmentId:
          departmentId != null && departmentId !== '' ? String(departmentId) : '',
        departmentName:
          cat.accountable_department_name ||
          cat.accountableDepartmentName ||
          cat.department_name ||
          cat.departmentName ||
          cat.accountable_department?.name ||
          cat.department?.name ||
          departmentNameById.get(String(departmentId)) ||
          '',
        active: cat.status === true,
      };
    });
  }, [categoriesRes, vendorNameById, departmentNameById]);

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    categoriesRes,
    { page, pageSize }
  );
  const pageItems = categories;

  const toggleActive = (cat) => {
    if (isUpdatingStatus) return;
    updateCategoryStatus({
      id: Number(cat.id),
      status: !cat.active,
    })
      .unwrap()
      .then(() => toast.success('Category status updated successfully.'))
      .catch((err) =>
        toast.error(err?.data?.message || 'Failed to update category status.')
      );
  };

  const openAdd = () => {
    setIsAddOpen(true);
    setNewCategoryName('');
    setNewCategoryComplainType('');
    setNewVendorId('');
    setNewDepartmentId('');
    setError('');
  };

  const closeAdd = () => {
    setIsAddOpen(false);
  };

  const openEdit = (cat) => {
    setIsEditOpen(true);
    setEditCategoryId(cat.id);
    setEditCategoryName(cat.name || '');
    const t = String(cat.category_complain_type || '').toUpperCase();
    setEditCategoryComplainType(t === 'CTM' || t === 'ITSM' ? t : '');
    setEditVendorId(cat.vendorId || '');
    setEditDepartmentId(cat.departmentId || '');
    setEditError('');
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditCategoryId(null);
    setEditCategoryName('');
    setEditCategoryComplainType('');
    setEditVendorId('');
    setEditDepartmentId('');
    setEditError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = newCategoryName.trim();
    const type = String(newCategoryComplainType || '').toUpperCase();
    const safeType = type === 'CTM' || type === 'ITSM' ? type : '';
    if (!safeType) {
      const msg = 'Please select Category Type';
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!newDepartmentId) {
      const msg = 'Please select Department';
      setError(msg);
      toast.error(msg);
      return;
    }
    const validationError = validateCategoryName(trimmed);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }
    const exists = categories.some(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      const msg = 'This category already exists';
      setError(msg);
      toast.error(msg);
      return;
    }
    try {
      await createCategory({
        name: trimmed,
        status: true,
        category_complain_type: safeType,
        accountable_department_id: Number(newDepartmentId),
        ...(newVendorId ? { vendor_id: Number(newVendorId) } : {}),
      }).unwrap();
      toast.success('Category created successfully.');
      setIsAddOpen(false);
      setNewCategoryName('');
      setNewCategoryComplainType('');
      setNewVendorId('');
      setNewDepartmentId('');
    } catch (err) {
      const msg = err?.data?.message || 'Failed to create category.';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const type = String(editCategoryComplainType || '').toUpperCase();
    const safeType = type === 'CTM' || type === 'ITSM' ? type : '';
    if (!safeType) {
      const msg = 'Please select Category Type';
      setEditError(msg);
      toast.error(msg);
      return;
    }
    if (!editDepartmentId) {
      const msg = 'Please select Department';
      setEditError(msg);
      toast.error(msg);
      return;
    }
    const trimmed = editCategoryName.trim();
    const validationError = validateCategoryName(trimmed);
    if (validationError) {
      setEditError(validationError);
      toast.error(validationError);
      return;
    }
    const exists = categories.some(
      (c) =>
        Number(c.id) !== Number(editCategoryId) &&
        c.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      const msg = 'This category already exists';
      setEditError(msg);
      toast.error(msg);
      return;
    }
    try {
      await updateCategory({
        id: Number(editCategoryId),
        name: trimmed,
        category_complain_type: safeType,
        accountable_department_id: Number(editDepartmentId),
        ...(editVendorId ? { vendor_id: Number(editVendorId) } : {}),
      }).unwrap();
      toast.success('Category updated successfully.');
      closeEdit();
    } catch (err) {
      const msg = err?.data?.message || 'Failed to update category.';
      setEditError(msg);
      toast.error(msg);
    }
  };

  return (
    <DashboardLayout title="Categories">
      <div className="space-y-4 py-4">

        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:items-center">
              <div className="relative w-full min-w-0 md:max-w-xs">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search Categories"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:flex-row md:items-center md:gap-2 md:w-56 md:shrink-0">
                <span className="shrink-0 font-medium">Filter By:</span>
                <div className="w-full min-w-0">
                  <SearchableSelect
                    name="filter"
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value || 'all');
                      setPage(1);
                    }}
                    placeholder="All"
                    clearable={false}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={STATUS_FILTER_OPTIONS}
                  />
                </div>
              </label>
            </div>
            {canCreate ? (
              <button
                type="button"
                className="inline-flex w-full shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm md:w-auto"
                onClick={openAdd}
              >
                + Add Categories
              </button>
            ) : null}
          </div>

          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Category Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Complaint Type
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                   Accountable Department
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Vendor
                  </th>
                  {hasActions ? (
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold text-gray-700">
                      Action
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={tableColSpan}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={categories.length > 0}
                  error={fetchError}
                  refetch={refetch}
                  message="Loading categories..."
                />
                {!fetchError && (categories.length > 0 || (!isLoading && !isFetching)) &&
                  pageItems.map((cat, index) => (
                    <tr key={cat.id} className="hover:bg-amber-50/40">
                      <td className="border-b border-gray-100 px-3 py-2">
                        {(currentPage - 1) * effectivePageSize + index + 1}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {cat.name}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {cat.category_complain_type || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {cat.departmentName || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2">
                        {cat.vendorName || '—'}
                      </td>
                      {hasActions ? (
                      <td className="border-b border-gray-100 px-3 py-2 text-right">
                        <div className="inline-flex items-center justify-end gap-3">
                          {canEdit ? (
                            <button
                              type="button"
                              disabled={isUpdating}
                              onClick={() => openEdit(cat)}
                              className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800 disabled:opacity-50"
                              aria-label="Edit category"
                              title="Edit"
                            >
                              <MdEdit className="h-5 w-5 text-red-700" />
                            </button>
                          ) : null}
                          {canDelete ? (
                          <button
                            type="button"
                            disabled={isUpdatingStatus}
                            onClick={() => toggleActive(cat)}
                            aria-label={
                              cat.active ? 'Disable category' : 'Enable category'
                            }
                            className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                              cat.active
                                ? 'border-emerald-600 bg-[#008E36]'
                                : 'border-gray-300 bg-gray-300'
                            }`}
                          >
                            <span
                              className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                cat.active ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                          ) : null}
                        </div>
                      </td>
                      ) : null}
                    </tr>
                  ))}
                {!fetchError && !isLoading && !isFetching && categories.length === 0 && (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="px-3 py-4 text-center text-sm text-gray-500"
                    >
                      No categories found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>

        {isAddOpen && (
          <div className="fixed inset-0 z-30 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="mb-4 flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-brand">Add Category</h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={closeAdd}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form
                onSubmit={handleSave}
                className="flex flex-col gap-4"
              >
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Category Type <span className="text-red-600">*</span>
                  </span>
                  <SearchableSelect
                    name="newCategoryComplainType"
                    value={newCategoryComplainType}
                    onChange={(e) => {
                      setNewCategoryComplainType(e.target.value);
                      setNewDepartmentId('');
                      setError('');
                    }}
                    placeholder="Select type"
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={[
                      { value: 'CTM', label: 'CTM' },
                      { value: 'ITSM', label: 'ITSM' },
                    ]}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                   Accountable Department <span className="text-red-600">*</span>
                  </span>
                  <SearchableSelect
                    name="newDepartmentId"
                    value={newDepartmentId}
                    onChange={(e) => {
                      setNewDepartmentId(e.target.value);
                      setError('');
                    }}
                    placeholder={
                      newCategoryComplainType
                        ? 'Select department'
                        : 'Select complaint type first'
                    }
                    disabled={!newCategoryComplainType}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={departmentOptions}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Vendor</span>
                  <SearchableSelect
                    name="newVendorId"
                    value={newVendorId}
                    onChange={(e) => {
                      setNewVendorId(e.target.value);
                      setError('');
                    }}
                    placeholder="Select vendor"
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={vendorOptions}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Category Name <span className="text-red-600">*</span>
                  </span>
                  <input
                    type="text"
                    placeholder="Enter Category Name"
                    value={newCategoryName}
                    maxLength={CATEGORY_NAME_MAX_LEN}
                    onChange={(e) => {
                      setNewCategoryName(e.target.value);
                      setError('');
                    }}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand placeholder:text-gray-600"
                  />
                </label>
                {error ? (
                  <p className="text-sm text-red-600">{error}</p>
                ) : null}
                <button
                  type="submit"
                  disabled={isCreating}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-50 sm:w-[140px]"
                >
                  {isCreating ? 'Saving...' : 'Save'}
                </button>
              </form>
            </div>
          </div>
        )}

        {isEditOpen && (
          <div className="fixed inset-0 z-30 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="mb-4 flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-brand">Edit Category</h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={closeEdit}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditSave} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Category Type <span className="text-red-600">*</span>
                  </span>
                  <SearchableSelect
                    name="editCategoryComplainType"
                    value={editCategoryComplainType}
                    onChange={(e) => {
                      setEditCategoryComplainType(e.target.value);
                      setEditDepartmentId('');
                      setEditError('');
                    }}
                    placeholder="Select type"
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={[
                      { value: 'CTM', label: 'CTM' },
                      { value: 'ITSM', label: 'ITSM' },
                    ]}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Accountable Department <span className="text-red-600">*</span>
                  </span>
                  <SearchableSelect
                    name="editDepartmentId"
                    value={editDepartmentId}
                    onChange={(e) => {
                      setEditDepartmentId(e.target.value);
                      setEditError('');
                    }}
                    placeholder={
                      editCategoryComplainType
                        ? 'Select department'
                        : 'Select complaint type first'
                    }
                    disabled={!editCategoryComplainType}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={departmentOptions}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>Vendor</span>
                  <SearchableSelect
                    name="editVendorId"
                    value={editVendorId}
                    onChange={(e) => {
                      setEditVendorId(e.target.value);
                      setEditError('');
                    }}
                    placeholder="Select vendor"
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={vendorOptions}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                  <span>
                    Category Name <span className="text-red-600">*</span>
                  </span>
                  <input
                    type="text"
                    placeholder="Enter Category Name"
                    value={editCategoryName}
                    maxLength={CATEGORY_NAME_MAX_LEN}
                    onChange={(e) => {
                      setEditCategoryName(e.target.value);
                      setEditError('');
                    }}
                    className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand placeholder:text-gray-600"
                  />
                </label>
                {editError && <p className="text-sm text-red-600">{editError}</p>}
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-50 sm:w-[140px]"
                >
                  {isUpdating ? 'Saving...' : 'Update'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AdminCategories;
